import React, { useState } from 'react';
import './ChatComponents.css';
import MarkdownRenderer from './MarkdownRenderer';

interface AnswerResult {
  isCorrect: boolean;
  correctAnswer: string;
  explanation: string;
  followUpPrompts?: Array<{ label: string; prompt: string; category: string }>;
}

interface ChatTrueFalseProps {
  componentId: string;
  statement: string;
  correctAnswer: string;
  explanation?: string;
  topic?: string;
  onAnswerSubmit: (componentId: string, answer: string) => Promise<AnswerResult | null>;
  onFollowUp?: (prompt: string) => void;
}

const ChatTrueFalse: React.FC<ChatTrueFalseProps> = ({
  componentId,
  statement,
  correctAnswer,
  explanation,
  topic,
  onAnswerSubmit,
  onFollowUp
}) => {
  const [selectedAnswer, setSelectedAnswer] = useState<'true' | 'false' | null>(null);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAnswerClick = async (answer: 'true' | 'false') => {
    if (result || isSubmitting) return;

    setSelectedAnswer(answer);
    setIsSubmitting(true);

    try {
      // Validate client-side — correct answer is already known
      const isCorrect = answer === correctAnswer.toLowerCase();
      const followUps = isCorrect
        ? [
            { label: 'Another T/F', prompt: 'Give me another true/false question', category: 'practice' },
            { label: 'Deeper dive', prompt: 'Tell me more about this concept', category: 'clarify' }
          ]
        : [
            { label: 'Explain why', prompt: `Why is the answer ${correctAnswer}? Help me understand.`, category: 'clarify' },
            { label: 'Try again', prompt: 'Give me a similar question to try again', category: 'practice' }
          ];
      setResult({
        isCorrect,
        correctAnswer,
        explanation: explanation || '',
        followUpPrompts: followUps,
      });

      // Attach the answer to this student + component: records the miss/hit to
      // analytics and feeds topic mastery. Fire-and-forget so instant client-
      // side feedback is never blocked on the network.
      if (onAnswerSubmit && componentId) {
        onAnswerSubmit(componentId, answer).catch(() => {});
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFollowUp = (prompt: string) => {
    if (onFollowUp) {
      onFollowUp(prompt);
    }
  };

  const getButtonClassName = (answer: 'true' | 'false') => {
    let className = `tf-option ${answer}`;

    if (selectedAnswer === answer) {
      className += ' selected';
    }

    if (result) {
      const correctAnswer = result.correctAnswer.toLowerCase();
      if (answer === correctAnswer) {
        className += ' correct';
      } else if (selectedAnswer === answer) {
        className += ' incorrect';
      }
    }

    return className;
  };

  return (
    <div className="chat-true-false">
      {/* Header */}
      {topic && (
        <div className="tf-header">
          <span className="tf-badge">{topic}</span>
          <span className="tf-type-badge">True/False</span>
        </div>
      )}

      {/* Statement */}
      <p className="tf-statement">{statement}</p>

      {/* Answer buttons */}
      <div className="tf-options">
        <button
          className={getButtonClassName('true')}
          onClick={() => handleAnswerClick('true')}
          disabled={!!result || isSubmitting}
        >
          <span className="tf-icon">✓</span>
          <span className="tf-label">True</span>
          {result && result.correctAnswer.toLowerCase() === 'true' && (
            <span className="tf-result-icon">✓</span>
          )}
        </button>
        <button
          className={getButtonClassName('false')}
          onClick={() => handleAnswerClick('false')}
          disabled={!!result || isSubmitting}
        >
          <span className="tf-icon">✗</span>
          <span className="tf-label">False</span>
          {result && result.correctAnswer.toLowerCase() === 'false' && (
            <span className="tf-result-icon">✓</span>
          )}
        </button>
      </div>

      {/* Loading indicator */}
      {isSubmitting && (
        <div className="tf-loading">Checking...</div>
      )}

      {/* Result panel */}
      {result && (
        <div className={`tf-result ${result.isCorrect ? 'correct' : 'incorrect'}`}>
          <div className="result-header">
            <span className="result-icon">{result.isCorrect ? '🎉' : '💡'}</span>
            <span className="result-text">
              {result.isCorrect ? 'Correct!' : 'The answer is ' + result.correctAnswer}
            </span>
          </div>
          {result.explanation && (
            <div className="result-explanation">
              <MarkdownRenderer>{result.explanation}</MarkdownRenderer>
            </div>
          )}
          {/* Follow-up prompts */}
          {result.followUpPrompts && result.followUpPrompts.length > 0 && (
            <div className="result-follow-ups">
              {result.followUpPrompts.map((fu, idx) => (
                <button
                  key={idx}
                  className="follow-up-chip"
                  onClick={() => handleFollowUp(fu.prompt)}
                >
                  {fu.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ChatTrueFalse;
